import { Region } from './region-model';
import { SelectorCountry } from './selector-country.model';
import { SelectorProvince } from './selector-province.model';
import { SelectorCity } from './region-selection-cities.model';

export class RegionSelection {
  private isCitiesEnabled = false;
  constructor(
    public regions: Map<string, Region> = new Map(),
    public countries: Map<string, SelectorCountry> = new Map(),
    public subnational: Map<string, SelectorProvince> = new Map(),
    public cities: Map<string, SelectorCity> = new Map(),
    public world: boolean = true
  ) {}

  public isCitySelected() {
    return this.cities.size;
  }

  public getName(regionNamesCount: number = 1): string {
    if (this.cities.size) {
      const [firstCity] = this.cities.values();
      return firstCity.name;
    }
    if (this.regions.size) {
      const [firstRegion] = this.regions.values();
      if (this.regions.size === 1) {
        return firstRegion.name;
      } else if (this.regions.size <= regionNamesCount) {
        return [...this.regions.values()]
          .map((region) => region.name)
          .join(', ');
      }

      return 'Multiple Regions';
    }

    if (this.countries.size) {
      const [firstCountry] = this.countries.values();
      if (this.countries.size === 1) {
        if (this.subnational.size) {
          const [firstProvince] = this.subnational.values();
          if (this.subnational.size === 1) {
            return `${firstCountry.name} - ${firstProvince.name}`;
          } else if (this.subnational.size <= regionNamesCount) {
            return `${firstCountry.name} - ${[...this.subnational.values()]
              .map((province) => province.name)
              .join(', ')}`;
          }

          return `${firstCountry.name} - Multiple Provinces`;
        }

        return firstCountry.name;
      } else if (this.countries.size <= regionNamesCount) {
        return [...this.countries.values()]
          .map((country) => country.name)
          .join(', ');
      }

      return 'Multiple Countries';
    }

    return 'The World';
  }

  public getRegionCodes(): string[] {
    return this.getCodes('region');
  }

  public getCountryCodes(): string[] {
    return this.getCodes('countries');
  }

  public getSubnationalCodes(): string[] {
    return this.getCodes('subnational');
  }

  public toggleWorld() {
    this.clearAll();
  }

  public toggleRegion(
    region: Region,
    countries: Map<string, SelectorCountry>,
    allowMultiSelect: boolean = false,
    isCitiesEnabled = false
  ) {
    this.isCitiesEnabled = isCitiesEnabled;
    if (allowMultiSelect) {
      this.subnational.clear();
      this.world = false;
      this._toggleRegion(region, countries, allowMultiSelect);
    } else {
      if (this.regions.has(region.name)) {
        this.clearAll();
      } else {
        this.clearAll();
        this._toggleRegion(region, countries, allowMultiSelect);
        this.world = false;
      }
    }
  }

  public toggleCountry(
    country: SelectorCountry,
    allowMultiSelect: boolean = false
  ) {
    if (allowMultiSelect) {
      this.toggleSelection('countries', country);
      this.world = false;
    } else {
      if (this.countries.has(country.code)) {
        this.clearAll();
      } else {
        this.clearAll();
        this.toggleSelection('countries', country);
        this.world = false;
      }
    }
  }

  public toggleProvince(
    province: SelectorProvince,
    allowMultiSelect: boolean = false
  ) {
    if (allowMultiSelect) {
      this.world = false;
      this.toggleSelection('subnational', province);
    } else {
      if (this.subnational.has(province.code)) {
        this.subnational.clear();
      } else {
        this.subnational.clear();
        this.toggleSelection('subnational', province);
        this.world = false;
      }
    }
  }

  public toggleCity(
    city?: SelectorCity,
    country?: SelectorCountry,
    allowMultiSelect: boolean = false
  ) {
    this.isCitiesEnabled = true;
    if (!city) {
      this.cities.clear();
      return;
    }
    if (this.cities.has(city.name)) {
      if (allowMultiSelect) {
        this.cities.delete(city.name);
        return;
      }
      this.cities.clear();
      return;
    }
    if (country) {
      if (!this.countries.has(country.name)) {
        this.countries.clear();
        this.toggleCountry(country, allowMultiSelect);
        this.world = false;
      }
    }
    if (!allowMultiSelect) {
      this.cities.clear();
    }
    this.toggleSelection('city', city);
  }

  public static copy(source: RegionSelection, target: RegionSelection) {
    target.regions = new Map(source.regions);
    target.countries = new Map(source.countries);
    target.subnational = new Map(source.subnational);
    target.cities = new Map(source.cities);
  }

  public clearAll(): void {
    this.world = true;
    this.regions.clear();
    this.countries.clear();
    this.subnational.clear();
    this.cities.clear();
  }

  public setFreemiumRegion(region: Region) {
    if (this.regions.has(region.name)) {
      this.regions.delete(region.name);
    } else {
      this.clearAll();
      this.regions.set(region.name, region);
    }
  }

  private setRegion(
    region: Region,
    countries: Map<string, SelectorCountry>,
    allowMultiSelect: boolean = false
  ) {
    this.regions.set(region.name, region);

    if (!this.isCitiesEnabled) {
      const isCountryEnabled = region.countries.some((code) =>
        this.countries.has(code)
      );
      region.countries.forEach((code) => {
        let country = countries.get(code);
        if (country && !country.authDisabled && !isCountryEnabled) {
          this.countries.set(code, country);
        } else {
          this.countries.delete(code);
        }
      });
    } else {
      if (allowMultiSelect) {
        this.countries = new Map([...this.countries, ...countries]);
        return;
      }
      this.countries = countries;
    }
  }

  private unsetRegion(
    region: Region,
    countries?: Map<string, SelectorCountry>
  ) {
    this.regions.delete(region.name);
    if (region.countries) {
      region.countries.forEach((country) => {
        this.countries.delete(country);
      });
      return;
    }
    if (countries && countries.size > 0) {
      countries.forEach((country) => {
        this.countries.delete(country.code);
      });
    }
  }

  private _toggleRegion(
    region: Region,
    countries: Map<string, SelectorCountry>,
    allowMultiSelect: boolean = false
  ) {
    let hasSelectedCountry = false;
    if (
      this.regions.size === 0 &&
      this.countries.size > 0 &&
      !allowMultiSelect
    ) {
      this.countries.clear();
      return;
    }
    if (allowMultiSelect) {
      this.countries.forEach((selectedCountries) => {
        countries.forEach((country) => {
          if (country.code === selectedCountries.code) {
            hasSelectedCountry = true;
          }
        });
      });
    }
    if (this.regions.has(region.name) || hasSelectedCountry) {
      this.unsetRegion(region, countries);
    } else {
      this.setRegion(region, countries, allowMultiSelect);
    }
  }

  private toggleSelection(
    regionType: 'countries' | 'subnational' | 'city',
    region: SelectorCountry | SelectorProvince | SelectorCity
  ) {
    let regionMap;
    switch (regionType) {
      case 'countries':
        regionMap = this.countries;
        break;
      case 'subnational':
        regionMap = this.subnational;
        break;
      case 'city':
        regionMap = this.cities;
        break;
      default:
        regionMap = this.subnational;
        break;
    }
    const code =
      regionType === 'city'
        ? region.name
        : (region as SelectorCountry | SelectorProvince).code;
    if (regionMap.has(code)) {
      regionMap.delete(code);
    } else {
      if (!region.authDisabled) {
        regionMap.set(code, region);
      }
    }
  }

  private getCodes(
    regionType: 'countries' | 'subnational' | 'region'
  ): string[] {
    if (regionType === 'countries') {
      return [...this.countries.values()].map((region) => region.code);
    } else if (regionType === 'subnational') {
      return [...this.subnational.values()].map((region) => region.code);
    } else {
      return [...this.regions.values()].map((region) => region.code);
    }
  }
}
